// src/pages/Outpaint.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateOutpaintModal from "../components/GenerateOutpaintModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import Loader from "../components/Loader"; // ✅ NEW

const FEATURE = "outpaint";
const PAGE_SIZE = 12;

/* ------------------------------ Simple toast ------------------------------ */
function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;

  return (
    <div className="fixed top-6 right-8 z-[9999] bg-pink-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fadeIn border border-pink-400">
      <AlertCircle size={20} />
      <span className="text-sm">{message}</span>
      <button
        className="ml-2 text-white hover:text-black transition"
        onClick={onClose}
        type="button"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default function Outpaint() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();
  const projectId = selectedProduct?.id || paramProjectId;

  const [project, setProject] = useState(null);
  const [assets, setAssets] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);
  const [toast, setToast] = useState("");

  // Quota (images) — mirrors other generators
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,

    // gating
    payment_blocked: false,
    sub_status: null,
    is_blocked: false,
  });

  // Infinite scroll
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  // Polling + lifecycle refs
  const previewBackdrop = useRef(null);
  const pollingTimers = useRef(new Map()); // assetId -> timerId
  const assetsRef = useRef(assets); // live pointer during intervals
  const mountedRef = useRef(false);

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  // eslint-disable-next-line no-unused-expressions
  void project;

  const isSoftDeleted = useCallback((a) => {
    const v = a?.metadata?.deleted;
    return v === true || v === "true";
  }, []);

  const isPendingStatus = useCallback((statusRaw) => {
    const s = String(statusRaw || "").toLowerCase();
    return s === "pending" || s === "queued" || s === "processing";
  }, []);

  /* ----------------------- Derived (feature-scoped) ----------------------- */
  const featureAssets = useMemo(
    () =>
      (assets || [])
        .filter(
          (a) => String(a?.metadata?.feature || "").toLowerCase() === FEATURE
        )
        .filter((a) => !isSoftDeleted(a)),
    [assets, isSoftDeleted]
  );

  const sortedFeatureAssets = useMemo(
    () =>
      [...featureAssets].sort(
        (a, b) =>
          new Date(b.updated_at || b.created_at || b.createdAt || 0) -
          new Date(a.updated_at || a.created_at || a.createdAt || 0)
      ),
    [featureAssets]
  );

  const visibleAssets = useMemo(
    () => sortedFeatureAssets.slice(0, page * PAGE_SIZE),
    [sortedFeatureAssets, page]
  );

  // ✅ make this stable (DO NOT depend on `page`)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  // Keep pageRef in sync (mirrors ObjectRemover)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change (mirrors ObjectRemover)
  useEffect(() => {
    recalcHasMore(sortedFeatureAssets, page);
  }, [sortedFeatureAssets, page, recalcHasMore]);

  /* -------------------------- Data fetching -------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/outpaint/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...data });
    } catch (_) {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  // NOTE: 'silent' prevents toggling the skeleton during polling refreshes.
  const fetchProject = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
      if (!projectId) {
        setProject(null);
        setProductImages([]);
        setAssets((prev) =>
          (prev || []).filter((a) => {
            const isTemp = String(a?.id || "").startsWith("temp-");
            const feat = String(a?.metadata?.feature || "").toLowerCase();
            return isTemp && feat === FEATURE;
          })
        );
        setError(null);

        // ✅ reset infinite scroll (mirrors ObjectRemover)
        setPage(1);
        pageRef.current = 1;

        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) {
        setInitialLoading(true);

        // ✅ reset infinite scroll on fresh fetch (mirrors ObjectRemover)
        setPage(1);
        pageRef.current = 1;
      }

      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!mountedRef.current) return;
        setProject(data);
        setAssets(Array.isArray(data.assets) ? data.assets : []);
        setProductImages(Array.isArray(data.images) ? data.images : []);
        setError(null);
      } catch {
        if (!mountedRef.current) return;
        setError("Failed to fetch project data");
      } finally {
        if (!silent && mountedRef.current) setInitialLoading(false);
      }
    },
    [getToken, projectId]
  );

  useEffect(() => {
    mountedRef.current = true;

    fetchProject(false);
    fetchQuota();

    const timersSnapshot = new Map(pollingTimers.current);
    return () => {
      mountedRef.current = false;
      timersSnapshot.forEach((tid) => clearInterval(tid));
      timersSnapshot.clear();
    };
  }, [fetchProject, fetchQuota]);

  /* ---------------------- Keep route in sync ---------------------- */
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/outpaint/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  /* ----------------------- Polling implementation ----------------------- */
  const startPollingAsset = useCallback(
    (assetId) => {
      if (!assetId || pollingTimers.current.has(assetId)) return;

      let attempts = 0;
      const maxAttempts = 80; // ~3.3 min @ 2.5s
      const tid = setInterval(async () => {
        attempts++;
        try {
          await fetchProject(true);

          const current = assetsRef.current || [];
          const match = current.find((x) => x.id === assetId);

          // Stop polling if deleted (soft delete)
          const deleted = match ? isSoftDeleted(match) : false;
          if (deleted) {
            clearInterval(tid);
            pollingTimers.current.delete(assetId);
            return;
          }

          const status = String(match?.metadata?.status || "").toLowerCase();
          const done = !!match && (status === "finished" || status === "done");
          const failed = !!match && (status === "failed" || status === "error");

          if (done || failed || attempts >= maxAttempts) {
            clearInterval(tid);
            pollingTimers.current.delete(assetId);
            fetchQuota();
          }
        } catch {
          // ignore transient poll errors
        }
      }, 2500);

      pollingTimers.current.set(assetId, tid);
    },
    [fetchProject, fetchQuota, isSoftDeleted]
  );

  /* ------------------ Auto-attach pollers for pending ------------------ */
  useEffect(() => {
    sortedFeatureAssets.forEach((a) => {
      const status = String(a?.metadata?.status || "").toLowerCase();
      const pending = isPendingStatus(status) || !a?.url;
      if (pending && a?.id) startPollingAsset(a.id);
    });
  }, [sortedFeatureAssets, isPendingStatus, startPollingAsset]);

  /* ---------------------- Generate (optimistic UI) ---------------------- */
  // ✅ CTA should be visible always; don’t mis-route while quota is still loading.
  const canGenerate =
    quota.loading ||
    (!quota?.is_blocked &&
      !quota?.payment_blocked &&
      (quota.unlimited || (quota.remaining || 0) > 0));

  const handleGenerate = async (imageUrl, aspectRatio) => {
    try {
      setError(null);
      setToast("");

      // ✅ No project yet → send user to Analyze (still shows CTA on the page)
      if (!projectId) {
        setToast("Please analyze a product first to start generating.");
        navigate("/analyze");
        return;
      }

      if (quota?.is_blocked) {
        setToast("Your account is blocked. Please contact support.");
        return;
      }
      if (quota?.payment_blocked) {
        navigate("/pricing");
        return;
      }
      if (!canGenerate) {
        navigate("/pricing");
        return;
      }

      const token = await getToken();

      const tempId = `temp-${Date.now()}`;
      setAssets((prev) => [
        {
          id: tempId,
          url: "/spinner.gif#pending",
          name: `outpaint-${tempId}`,
          type: "photo",
          metadata: { feature: FEATURE, status: "pending", local: true },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        ...prev,
      ]);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/outpaint/extend`,
        { imageUrl, projectId, aspectRatio },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const pendingAsset = data?.asset;
      if (!pendingAsset?.id) {
        setAssets((prev) => prev.filter((a) => a.id !== tempId));
        setError("Failed to queue job.");
        setToast("Failed to queue job.");
        return;
      }

      setAssets((prev) => [
        pendingAsset,
        ...prev.filter((a) => a.id !== tempId),
      ]);

      // Optimistic quota decrement (pending counts)
      if (!quota.unlimited) {
        setQuota((q) => ({
          ...q,
          remaining: Math.max(0, (q.remaining || 0) - 1),
          used: (q.used || 0) + 1,
        }));
      }

      startPollingAsset(pendingAsset.id);
    } catch (e) {
      setAssets((prev) =>
        prev.filter(
          (a) => !(a.metadata?.local && a.metadata?.feature === FEATURE)
        )
      );

      const code = e?.response?.data?.code;
      if (code === "payment_required" || code === "images_quota_exceeded") {
        navigate("/pricing");
        return;
      }

      const msg = e?.response?.data?.error || "Failed to outpaint image.";
      setError(msg);
      setToast(msg);
      fetchQuota();
    }
  };

  /* --------------------------- Utilities --------------------------- */
  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse errors
    }
    return url;
  }, []);

  const handleDownload = (url) => {
    const dl = toCloudinaryDownloadUrl(url);
    const a = document.createElement("a");
    a.href = dl;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (assetId) => {
    if (!assetId) return;

    // Local temp card
    if (String(assetId).startsWith("temp-")) {
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      return;
    }

    if (!window.confirm("Delete this image?")) return;
    try {
      const token = await getToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/outpaint/${assetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const tid = pollingTimers.current.get(assetId);
      if (tid) {
        clearInterval(tid);
        pollingTimers.current.delete(assetId);
      }

      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      if (preview?.id === assetId) setPreview(null);
      fetchQuota();
    } catch {
      alert("Delete failed");
    }
  };

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  // Preview close on Esc / click-outside
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current)
        setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  /* --------------- Quota display helpers --------------- */
  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);
  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  // ✅ Don’t block CTA behind quota.loading (Remove BG behavior)
  const isInitialLoading = initialLoading && !error && visibleAssets.length === 0;

  /* ------------------------------ UI ------------------------------ */
  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <Toast message={toast} onClose={() => setToast("")} />

      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Extend Image (Outpaint)
            </h1>
            <p className="text-white/70">
              Uncrop and expand images while keeping the look consistent.
            </p>
          </div>

          {/* Quota pill */}
          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold">
                  Unlimited images
                </span>
              ) : (
                <span className="text-white/80">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">
                    {totalImages === Infinity ? "∞" : totalImages}
                  </span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Usage bar */}
        {showBar && (
          <div className="mb-4">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages === Infinity ? "∞" : totalImages}</span>
            </div>
          </div>
        )}

        {error && <div className="text-red-400 mb-6">{error}</div>}

        {/* Grid / Loader */}
        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : (
          // ✅ CTA always visible (same behavior as RemoveBackground)
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* ✅ Create Button (always visible; redirects like Remove BG) */}
            <div
              onClick={() => {
                // ✅ No analyzed product → go Analyze
                if (!projectId) {
                  navigate("/analyze");
                  return;
                }

                // ✅ No quota / blocked / payment → go Pricing
                if (!canGenerate) {
                  navigate("/pricing");
                  return;
                }

                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg h-[300px] min-h-[160px] transition text-center p-6 ${
                !projectId || !canGenerate
                  ? "opacity-60 cursor-not-allowed hover:border-[#3A3A50] hover:text-white"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              }`}
              title={
                !projectId
                  ? "Analyze a product first to start generating"
                  : !canGenerate
                  ? "Out of images. Click to manage plan."
                  : "Choose an image and new aspect ratio"
              }
            >
              <Plus className="w-8 h-8 mb-2" />
              <p className="font-semibold text-base">Outpaint Image</p>
              <p className="text-sm text-gray-400 mt-1">
                {!projectId
                  ? "Analyze your first product to get started"
                  : quota.unlimited
                  ? "Unlimited images"
                  : quota.loading
                  ? "Checking quota…"
                  : `You have ${remaining} left of ${
                      totalImages === Infinity ? "∞" : totalImages
                    } this cycle`}
              </p>
              {!!projectId && !canGenerate && !quota.loading && (
                <p className="text-xs text-red-300 mt-2">
                  You’ve reached your image limit. Upgrade or add images.
                </p>
              )}
            </div>

            {/* Assets */}
            {visibleAssets.map((a) => {
              const status = String(a?.metadata?.status || "").toLowerCase();
              const isPending = isPendingStatus(status) || !a?.url;
              const failed = status === "failed" || status === "error";
              const url = isPending ? "/spinner.gif" : a.url;

              return (
                <div
                  key={a.id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden"
                >
                  {/* delete */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(a.id);
                    }}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition z-20"
                    title="Delete"
                    type="button"
                  >
                    <Trash2 size={16} />
                  </button>

                  {failed ? (
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <div className="flex items-center gap-2 text-pink-400">
                        <AlertCircle size={28} className="mb-1" />
                        <span className="font-semibold text-base">
                          Generation Failed
                        </span>
                      </div>
                      <div className="text-xs text-pink-300 mt-2 text-center max-w-[180px] mx-auto">
                        {a.metadata?.reason || "Could not outpaint image."}
                      </div>
                      <button
                        onClick={() => handleDelete(a.id)}
                        className="mt-3 px-3 py-1 bg-pink-700/70 hover:bg-red-700 text-white rounded-lg text-xs shadow transition"
                        type="button"
                      >
                        Delete
                      </button>
                    </div>
                  ) : isPending ? (
                    <img
                      src="/spinner.gif"
                      alt="Generating..."
                      className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                      draggable={false}
                      style={{
                        background: "#111124",
                        height: "100%",
                        maxHeight: "calc(100% - 32px)",
                      }}
                    />
                  ) : (
                    <>
                      <img
                        src={url}
                        alt={a.name || "Outpainted"}
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ url, id: a.id })}
                        onError={(e) => {
                          // eslint-disable-next-line no-param-reassign
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-10"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(url);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                            type="button"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Infinite loader */}
        {hasMore && !initialLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img
                src="/spinner.gif"
                alt="Loading..."
                className="w-7 h-7 mx-auto"
              />
            )}
          </div>
        )}

        {modalOpen && (
          <GenerateOutpaintModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
          />
        )}

        {/* Preview modal */}
        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
                type="button"
              >
                <X size={21} />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>
              <div className="flex justify-center gap-4 pb-5">
                <button
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                  type="button"
                >
                  Download
                </button>
                <button
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                  type="button"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}

        {error && <div className="text-red-500 mt-6">{error}</div>}
      </div>
    </div>
  );
}
